import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { shootService } from '../services/shootService'
import { clientService } from '../services/clientService'
import { invoiceService } from '../services/invoiceService'
import { analyticsService } from '../services/analyticsService'
import {
  SparklesIcon,
  XMarkIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ClockIcon,
  BoltIcon,
  LightBulbIcon,
  ArrowTrendingUpIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline'

interface AIBusinessAssistantProps {
  isOpen: boolean
  onClose: () => void
}

interface AIRecommendation {
  id: string
  type: 'pricing' | 'scheduling' | 'marketing' | 'workflow' | 'growth'
  priority: 'high' | 'medium' | 'low'
  title: string
  description: string
  impact: 'revenue' | 'efficiency' | 'client_satisfaction' | 'time_saving'
  estimatedValue: number
  actionItems: string[]
  confidence: number
  isPersonalized: boolean
}

interface UserPreferences {
  primaryGoals: string[]
  interestedNiches: string[]
  currentChallenges: string[]
  businessStage: 'starting' | 'growing' | 'established' | 'scaling'
  weeklyHours: number
  priceRange: string
}

interface QuestionnaireFormProps {
  onSubmit: (preferences: UserPreferences) => void
  onSkip: () => void
}

const QuestionnaireForm = ({ onSubmit, onSkip }: QuestionnaireFormProps) => {
  const [step, setStep] = useState(0)
  const [formData, setFormData] = useState<Partial<UserPreferences>>({})

  const questions = [
    {
      id: 'primaryGoals',
      title: 'What are your main business goals?',
      type: 'multiple',
      options: [
        { value: 'increase_revenue', label: 'Increase Revenue' },
        { value: 'save_time', label: 'Save Time' },
        { value: 'get_more_clients', label: 'Get More Clients' },
        { value: 'improve_quality', label: 'Improve Service Quality' },
        { value: 'scale_business', label: 'Scale My Business' }
      ]
    },
    {
      id: 'businessStage',
      title: 'What stage is your business at?',
      type: 'single',
      options: [
        { value: 'starting', label: 'Just Starting (0-6 months)' },
        { value: 'growing', label: 'Growing (6 months - 2 years)' },
        { value: 'established', label: 'Established (2-5 years)' },
        { value: 'scaling', label: 'Scaling (5+ years)' }
      ]
    },
    {
      id: 'interestedNiches',
      title: 'Which photography niches interest you?',
      type: 'multiple',
      options: [
        { value: 'Wedding Photography', label: 'Wedding Photography' },
        { value: 'Portrait Photography', label: 'Portrait Photography' },
        { value: 'Corporate Photography', label: 'Corporate Photography' },
        { value: 'Event Photography', label: 'Event Photography' },
        { value: 'Product Photography', label: 'Product Photography' },
        { value: 'Real Estate Photography', label: 'Real Estate Photography' }
      ]
    },
    {
      id: 'currentChallenges',
      title: 'What are your biggest challenges?',
      type: 'multiple',
      options: [
        { value: 'finding_clients', label: 'Finding New Clients' },
        { value: 'pricing', label: 'Pricing My Services' },
        { value: 'time_management', label: 'Time Management' },
        { value: 'client_communication', label: 'Client Communication' },
        { value: 'workflow_efficiency', label: 'Workflow Efficiency' }
      ]
    },
    {
      id: 'weeklyHours',
      title: 'How many hours per week do you work?',
      type: 'single',
      options: [
        { value: 20, label: 'Part-time (10-25 hours)' },
        { value: 35, label: 'Full-time (30-40 hours)' },
        { value: 50, label: 'More than full-time (40+ hours)' }
      ]
    },
    {
      id: 'priceRange',
      title: 'What\'s your typical project price range?',
      type: 'single',
      options: [
        { value: '500-1000', label: '$500 - $1,000' },
        { value: '1000-3000', label: '$1,000 - $3,000' },
        { value: '3000-5000', label: '$3,000 - $5,000' },
        { value: '5000+', label: '$5,000+' }
      ]
    }
  ]

  const currentQuestion = questions[step]

  const handleAnswer = (value: any) => {
    const newData = { ...formData }
    
    if (currentQuestion.type === 'multiple') {
      const currentValues = newData[currentQuestion.id as keyof UserPreferences] as string[] || []
      if (currentValues.includes(String(value))) {
        newData[currentQuestion.id as keyof UserPreferences] = currentValues.filter(v => v !== String(value)) as any
      } else {
        newData[currentQuestion.id as keyof UserPreferences] = [...currentValues, String(value)] as any
      }
    } else {
      newData[currentQuestion.id as keyof UserPreferences] = value as any
    }
    
    setFormData(newData)
  }

  const handleNext = () => {
    if (step < questions.length - 1) {
      setStep(step + 1)
    } else {
      // Submit form
      const completeData: UserPreferences = {
        primaryGoals: formData.primaryGoals || [],
        interestedNiches: formData.interestedNiches || [],
        currentChallenges: formData.currentChallenges || [],
        businessStage: formData.businessStage || 'growing',
        weeklyHours: formData.weeklyHours || 30,
        priceRange: formData.priceRange || '1000-3000'
      }
      onSubmit(completeData)
    }
  }

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1)
    }
  }

  const isAnswered = () => {
    const answer = formData[currentQuestion.id as keyof UserPreferences]
    if (currentQuestion.type === 'multiple') {
      return Array.isArray(answer) && answer.length > 0
    }
    return answer !== undefined
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex justify-between text-sm text-gray-400 mb-2">
          <span>Question {step + 1} of {questions.length}</span>
          <span>{Math.round(((step + 1) / questions.length) * 100)}%</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((step + 1) / questions.length) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Question */}
      <div className="mb-8">
        <h4 className="text-xl font-bold text-white mb-6">{currentQuestion.title}</h4>
        
        <div className="space-y-3">
          {currentQuestion.options.map((option) => {
            const isSelected = currentQuestion.type === 'multiple' 
              ? (formData[currentQuestion.id as keyof UserPreferences] as string[] || []).includes(String(option.value))
              : formData[currentQuestion.id as keyof UserPreferences] === option.value

            return (
              <button
                key={option.value}
                onClick={() => handleAnswer(option.value)}
                className={`w-full p-4 text-left rounded-lg border transition-all ${
                  isSelected
                    ? 'bg-purple-500/20 border-purple-500/40 text-white'
                    : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:border-white/20'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span>{option.label}</span>
                  {isSelected && (
                    <CheckIcon className="w-5 h-5 text-purple-400" />
                  )}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex space-x-3">
          {step > 0 && (
            <button
              onClick={handleBack}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
            >
              Back
            </button>
          )}
          <button
            onClick={onSkip}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Skip Survey
          </button>
        </div>
        
        <button
          onClick={handleNext}
          disabled={!isAnswered()}
          className={`px-6 py-2 rounded-lg font-medium transition-all ${
            isAnswered()
              ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          {step === questions.length - 1 ? 'Get My Recommendations' : 'Next'}
        </button>
      </div>
    </div>
  )
}

const AIBusinessAssistant = ({ isOpen, onClose }: AIBusinessAssistantProps) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [recommendations, setRecommendations] = useState<AIRecommendation[]>([])
  const [selectedRecommendation, setSelectedRecommendation] = useState<AIRecommendation | null>(null)
  const [implementedActions, setImplementedActions] = useState<Set<string>>(new Set())
  const [showQuestionnaire, setShowQuestionnaire] = useState(false)
  const [preferences, setPreferences] = useState<UserPreferences | null>(null)
  const [userData, setUserData] = useState<any>(null)

  useEffect(() => {
    if (isOpen && user?.id) {
      loadUserData()
      loadUserPreferences()
    }
  }, [isOpen, user?.id])

  const loadUserData = async () => {
    if (!user?.id) return
    
    try {
      const [shoots, clients, incomeStats, projectedIncome] = await Promise.all([
        shootService.getShootsForUser(user.id),
        clientService.getClientsForUser(user.id),
        invoiceService.getInvoiceStats(user.id),
        analyticsService.calculateProjectedAnnualIncome(user.id)
      ])
      
      setUserData({
        shoots,
        clients,
        incomeStats: incomeStats.success ? incomeStats.stats : null,
        projectedIncome
      })
    } catch (error) {
      console.error('Error loading user data:', error)
    }
  }

  const loadUserPreferences = () => {
    // Load preferences from localStorage or show questionnaire
    const saved = localStorage.getItem(`ai_preferences_${user?.id}`)
    if (saved) {
      const parsedPrefs = JSON.parse(saved)
      setPreferences(parsedPrefs)
      generatePersonalizedRecommendations(parsedPrefs)
    } else {
      setShowQuestionnaire(true)
      setLoading(false)
    }
  }

  const generatePersonalizedRecommendations = async (prefs: UserPreferences) => {
    try {
      setLoading(true)
      
      const personalizedRecs: AIRecommendation[] = []
      
      // Revenue-focused recommendations
      if (prefs.primaryGoals.includes('increase_revenue')) {
        if (userData?.incomeStats) {
          const avgProjectValue = userData.incomeStats.totalAmount / Math.max(userData.incomeStats.totalInvoices, 1)
          
          personalizedRecs.push({
            id: 'pricing_optimization',
            type: 'pricing',
            priority: 'high',
            title: 'Optimize Your Pricing Strategy',
            description: `Based on your current average project value of $${Math.round(avgProjectValue)}, market analysis suggests you can increase rates by 15-20% without losing clients.`,
            impact: 'revenue',
            estimatedValue: Math.round(avgProjectValue * 0.15 * userData.incomeStats.totalInvoices),
            actionItems: [
              `Increase your base rate to $${Math.round(avgProjectValue * 1.15)}`,
              'Create premium packages for high-value clients',
              'Implement seasonal pricing for peak periods'
            ],
            confidence: 85,
            isPersonalized: true
          })
        }
      }
      
      // Efficiency recommendations
      if (prefs.primaryGoals.includes('save_time') || prefs.currentChallenges.includes('time_management')) {
        personalizedRecs.push({
          id: 'workflow_automation',
          type: 'workflow',
          priority: 'high',
          title: 'Automate Your Client Workflow',
          description: `You're spending approximately ${prefs.weeklyHours > 40 ? '15-20' : '8-12'} hours per week on admin tasks. Automation can reduce this by 70%.`,
          impact: 'time_saving',
          estimatedValue: Math.round((prefs.weeklyHours * 0.3) * 52 * 50), // Time value estimate
          actionItems: [
            'Set up automated client onboarding emails',
            'Create template contracts for different shoot types',
            'Implement automatic invoice reminders'
          ],
          confidence: 92,
          isPersonalized: true
        })
      }
      
      // Growth recommendations based on business stage
      if (prefs.businessStage === 'growing' || prefs.businessStage === 'scaling') {
        personalizedRecs.push({
          id: 'niche_expansion',
          type: 'growth',
          priority: 'medium',
          title: `Expand into ${prefs.interestedNiches[0] || 'Corporate Photography'}`,
          description: `Market demand for ${prefs.interestedNiches[0] || 'corporate photography'} in your area is strong. This could add 25-40% to your annual revenue.`,
          impact: 'revenue',
          estimatedValue: userData?.projectedIncome?.projectedAnnualIncome ? 
            Math.round(userData.projectedIncome.projectedAnnualIncome * 0.3) : 15000,
          actionItems: [
            `Create a portfolio showcasing ${prefs.interestedNiches[0] || 'corporate'} work`,
            'Network with local businesses and event planners',
            `Develop specialized packages for ${prefs.interestedNiches[0] || 'corporate clients'}`
          ],
          confidence: 78,
          isPersonalized: true
        })
      }
      
      // Client retention recommendations
      if (userData?.clients && userData.clients.length > 5) {
        const recentClients = userData.clients.filter((client: any) => 
          new Date(client.created_at) > new Date(Date.now() - 6 * 30 * 24 * 60 * 60 * 1000)
        ).length
        
        if (recentClients < userData.clients.length * 0.3) {
          personalizedRecs.push({
            id: 'client_retention',
            type: 'marketing',
            priority: 'high',
            title: 'Re-engage Past Clients',
            description: `You have ${userData.clients.length - recentClients} past clients who haven't booked recently. A targeted campaign could recover 20-30% of them.`,
            impact: 'client_satisfaction',
            estimatedValue: Math.round((userData.clients.length - recentClients) * 0.25 * 800), // Estimated rebooking value
            actionItems: [
              'Send personalized "we miss you" emails with recent work',
              'Offer loyalty discounts for returning clients',
              'Create seasonal promotions for past clients'
            ],
            confidence: 82,
            isPersonalized: true
          })
        }
      }
      
      // Challenge-specific recommendations
      if (prefs.currentChallenges.includes('finding_clients')) {
        personalizedRecs.push({
          id: 'client_acquisition',
          type: 'marketing',
          priority: 'high',
          title: 'Boost Your Client Acquisition',
          description: 'Based on your current client base, strategic marketing efforts can increase your booking rate by 40-60%.',
          impact: 'revenue',
          estimatedValue: 8500,
          actionItems: [
            'Optimize your Instagram presence with consistent posting',
            'Ask satisfied clients for Google reviews and referrals',
            'Partner with local wedding vendors for cross-referrals'
          ],
          confidence: 75,
          isPersonalized: true
        })
      }
      
      // Add some general recommendations if needed
      if (personalizedRecs.length < 4) {
        personalizedRecs.push({
          id: 'social_proof',
          type: 'marketing',
          priority: 'medium',
          title: 'Build Social Proof Strategy',
          description: 'Testimonials and case studies can increase your booking rate by 35% and justify premium pricing.',
          impact: 'client_satisfaction',
          estimatedValue: 4200,
          actionItems: [
            'Create a client testimonial collection process',
            'Develop case studies for your best projects',
            'Feature client success stories on your website'
          ],
          confidence: 88,
          isPersonalized: false
        })
      }
      
      setRecommendations(personalizedRecs)
    } catch (error) {
      console.error('Error generating recommendations:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleQuestionnaireSubmit = (prefs: UserPreferences) => {
    setPreferences(prefs)
    setShowQuestionnaire(false)
    localStorage.setItem(`ai_preferences_${user?.id}`, JSON.stringify(prefs))
    generatePersonalizedRecommendations(prefs)
  }

  const resetPreferences = () => {
    localStorage.removeItem(`ai_preferences_${user?.id}`)
    setPreferences(null)
    setRecommendations([])
    setShowQuestionnaire(true)
  }

  const handleImplementAction = (recommendationId: string, actionIndex: number) => {
    const actionKey = `${recommendationId}-${actionIndex}`
    const newImplemented = new Set(implementedActions)
    newImplemented.add(actionKey)
    setImplementedActions(newImplemented)
  }

  const getImpactIcon = (impact: string) => {
    switch (impact) {
      case 'revenue':
        return <CurrencyDollarIcon className="w-5 h-5 text-green-400" />
      case 'efficiency':
        return <BoltIcon className="w-5 h-5 text-blue-400" />
      case 'client_satisfaction':
        return <UserGroupIcon className="w-5 h-5 text-purple-400" />
      case 'time_saving':
        return <ClockIcon className="w-5 h-5 text-orange-400" />
      default:
        return <ChartBarIcon className="w-5 h-5 text-gray-400" />
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-400 bg-red-500/20'
      case 'medium':
        return 'text-yellow-400 bg-yellow-500/20'
      case 'low':
        return 'text-green-400 bg-green-500/20'
      default:
        return 'text-gray-400 bg-gray-500/20'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="relative w-full max-w-6xl max-h-[90vh] glass-mockup rounded-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
              <SparklesIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">AI Business Assistant</h2>
              <p className="text-gray-400 text-sm">Intelligent recommendations for your photography business</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex h-[calc(90vh-120px)]">
          {/* Questionnaire */}
          {showQuestionnaire && (
            <div className="w-full p-6 overflow-y-auto">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4">
                  <QuestionMarkCircleIcon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Let's Personalize Your AI Assistant</h3>
                <p className="text-gray-400">Answer a few questions to get tailored business recommendations</p>
              </div>

              <QuestionnaireForm 
                onSubmit={handleQuestionnaireSubmit}
                onSkip={() => {
                  const defaultPrefs: UserPreferences = {
                    primaryGoals: ['increase_revenue'],
                    interestedNiches: ['Wedding Photography'],
                    currentChallenges: ['finding_clients'],
                    businessStage: 'growing',
                    weeklyHours: 30,
                    priceRange: '1000-3000'
                  }
                  handleQuestionnaireSubmit(defaultPrefs)
                }}
              />
            </div>
          )}

          {/* Recommendations List */}
          {!showQuestionnaire && (
            <>
              <div className="w-1/2 p-6 border-r border-white/10 overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Your Recommendations</h3>
                    {preferences && (
                      <p className="text-gray-400 text-sm">Personalized for your {preferences.businessStage} business</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {loading && (
                      <div className="animate-spin w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full"></div>
                    )}
                    <button
                      onClick={resetPreferences}
                      className="text-gray-400 hover:text-white text-xs"
                    >
                      Retake Survey
                    </button>
                  </div>
                </div>

                {loading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="p-4 bg-white/5 rounded-lg animate-pulse">
                        <div className="h-4 bg-white/20 rounded mb-2"></div>
                        <div className="h-3 bg-white/10 rounded mb-2"></div>
                        <div className="h-3 bg-white/10 rounded w-3/4"></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {recommendations.map((rec) => (
                      <motion.div
                        key={rec.id}
                        whileHover={{ scale: 1.02 }}
                        onClick={() => setSelectedRecommendation(rec)}
                        className={`p-4 rounded-lg cursor-pointer transition-all relative ${
                          selectedRecommendation?.id === rec.id
                            ? 'bg-purple-500/20 border border-purple-500/40'
                            : 'bg-white/5 hover:bg-white/10 border border-transparent'
                        }`}
                      >
                        {/* Personalized indicator */}
                        {rec.isPersonalized && (
                          <div className="absolute top-2 right-2 w-2 h-2 bg-purple-400 rounded-full"></div>
                        )}
                        
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            {getImpactIcon(rec.impact)}
                            <span className="text-white font-medium text-sm">{rec.title}</span>
                            {rec.isPersonalized && (
                              <span className="text-xs px-2 py-1 bg-purple-500/30 text-purple-300 rounded-full">
                                Personalized
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(rec.priority)}`}>
                              {rec.priority}
                            </span>
                            <div className="text-green-400 font-bold text-sm">
                              {formatCurrency(rec.estimatedValue)}
                            </div>
                          </div>
                        </div>
                        
                        <p className="text-gray-300 text-sm mb-3">{rec.description}</p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1">
                            <span className="text-gray-400 text-xs">Confidence:</span>
                            <div className="w-12 h-2 bg-gray-700 rounded-full">
                              <div 
                                className="h-2 bg-purple-400 rounded-full transition-all"
                                style={{ width: `${rec.confidence}%` }}
                              ></div>
                            </div>
                            <span className="text-purple-400 text-xs font-medium">{rec.confidence}%</span>
                          </div>
                          <span className="text-gray-400 text-xs">{rec.actionItems.length} actions</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recommendation Details */}
              <div className="w-1/2 p-6 overflow-y-auto">
                {selectedRecommendation ? (
                  <div>
                    <div className="flex items-center space-x-3 mb-6">
                      {getImpactIcon(selectedRecommendation.impact)}
                      <h3 className="text-xl font-bold text-white">{selectedRecommendation.title}</h3>
                      {selectedRecommendation.isPersonalized && (
                        <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm font-medium">
                          Tailored for You
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="p-4 bg-white/5 rounded-lg">
                        <div className="flex items-center space-x-2 mb-2">
                          <ArrowTrendingUpIcon className="w-4 h-4 text-green-400" />
                          <span className="text-gray-400 text-sm">Estimated Value</span>
                        </div>
                        <p className="text-2xl font-bold text-green-400">
                          {formatCurrency(selectedRecommendation.estimatedValue)}
                        </p>
                      </div>
                      
                      <div className="p-4 bg-white/5 rounded-lg">
                        <div className="flex items-center space-x-2 mb-2">
                          <LightBulbIcon className="w-4 h-4 text-purple-400" />
                          <span className="text-gray-400 text-sm">Confidence Level</span>
                        </div>
                        <p className="text-2xl font-bold text-purple-400">
                          {selectedRecommendation.confidence}%
                        </p>
                      </div>
                    </div>

                    <div className="mb-6">
                      <h4 className="text-white font-semibold mb-3">Description</h4>
                      <p className="text-gray-300 leading-relaxed">{selectedRecommendation.description}</p>
                    </div>

                    <div>
                      <h4 className="text-white font-semibold mb-4 flex items-center space-x-2">
                        <CheckIcon className="w-4 h-4 text-green-400" />
                        <span>Action Items</span>
                      </h4>
                      <div className="space-y-3">
                        {selectedRecommendation.actionItems.map((action, index) => {
                          const actionKey = `${selectedRecommendation.id}-${index}`
                          const isImplemented = implementedActions.has(actionKey)
                          
                          return (
                            <div
                              key={index}
                              className={`p-3 rounded-lg border transition-all ${
                                isImplemented
                                  ? 'bg-green-500/10 border-green-500/30'
                                  : 'bg-white/5 border-white/10'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <p className={`text-sm ${
                                  isImplemented ? 'text-green-300' : 'text-gray-300'
                                }`}>
                                  {action}
                                </p>
                                <button
                                  onClick={() => handleImplementAction(selectedRecommendation.id, index)}
                                  disabled={isImplemented}
                                  className={`ml-3 px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                                    isImplemented
                                      ? 'bg-green-500/20 text-green-400 cursor-not-allowed'
                                      : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                                  }`}
                                >
                                  {isImplemented ? 'Done' : 'Mark Complete'}
                                </button>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>

                    {selectedRecommendation.priority === 'high' && (
                      <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <div className="flex items-start space-x-3">
                          <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mt-0.5" />
                          <div>
                            <p className="text-red-400 font-medium mb-1">High Priority</p>
                            <p className="text-gray-300 text-sm">
                              This recommendation has significant impact potential. Consider implementing soon for maximum benefit.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-center">
                    <div>
                      <SparklesIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-white text-lg font-medium mb-2">Select a Recommendation</h3>
                      <p className="text-gray-400">
                        Choose a recommendation from the list to see detailed insights and action items.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  )
}

export default AIBusinessAssistant 